import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import {
  Logging,
  Notification,
  NotificationData,
  CustomerStatus,
  stringToDate,
  CustomerApproveConfirmation,
  API,
  ResponseError,
  UserInfo,
  UserContext,
  PermissionGroup,
  getEditPermission,
  Certificate,
  getCurrentDateOnly,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CustomerViewProps from './CustomerViewProps.interface';
import '../../customer-info.scss';
import CustomerViewTabs from '../CustomerViewTabs/CustomerViewTabs';

const CustomerView = ({
  gatewayEndPoint,
  customer,
  uuid,
  initialSelectedTab,
  refreshCustomer,
}: CustomerViewProps): JSX.Element => {
  const hasParentCustomer = customer ? customer.parent !== null : false;
  const [selectedTab, setSelectedTab] = React.useState(initialSelectedTab);
  const [redirect, setRedirect] = React.useState(null);
  const [isCustomerApproveConfirmationOpen, setIsCustomerApproveConfirmationOpen] = React.useState(false);
  const [notification, setNotification] = React.useState(null);
  const [customerWasApproved, setCustomerWasApproved] = React.useState(null);

  const { ccPreventEcmEdits } = useFlags();

  const dispatch = useDispatch();

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Customers Edit Permission
  const permissionCustomersEdit: boolean = getEditPermission(userContext, PermissionGroup.Customers);

  // Tab Selected
  const onSelectTab = (tab: string): void => {
    setSelectedTab(tab);
  };

  // Make the API Call to Accept the Certificate
  const approveCustomer = () => async (): Promise<void> => {
    Logging.Info('Approve Customer');
    try {
      const api = new API(gatewayEndPoint);

      await api.customer.approveCustomerV4(customer.uuid);

      // Update the Customer Status Indicator
      setCustomerWasApproved(true);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Approval Error',
        description: `Unable to approve Customer '${customer.name}'.`,
        error: error as ResponseError,
      };
      setNotification(notificationData);
    }
  };

  // Approve Customer
  const onApprove = (): void => {
    if (hasParentCustomer) {
      // Show the Approve Confirmation Dialog
      setIsCustomerApproveConfirmationOpen(true);
    } else {
      // Dispatch the Asynchronous Event
      dispatch(approveCustomer());
    }
  };

  const onApproveConfirmation = (): void => {
    Logging.Info('Approve Confirmation');

    // Hide the Approve Confirmation Dialog
    setIsCustomerApproveConfirmationOpen(false);

    // Dispatch the Asynchronous Event
    dispatch(approveCustomer());
  };

  const onCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');

    // Hide the Approve Confirmation Dialog
    setIsCustomerApproveConfirmationOpen(false);
  };

  // Edit Customer
  const onEditCustomer = (): void => {
    Logging.Info('Edit Customer');

    // Direct to Edit Customers
    setRedirect(`/customer/edit/${uuid}`);
  };

  // Add Certificate
  const onAddCertificate = (): void => {
    Logging.Info('Add Certificate');
  };

  // Get Customer Status
  const getCustomerStatus = (): CustomerStatus => {
    if (customer) {
      let status = customerWasApproved
        ? CustomerStatus.Approved // Customer was just Approved
        : customer.status || CustomerStatus.Pending; // Customer Status or Pending if not defined

      // If Approved Status break it down into Active or Inactive
      if (status === CustomerStatus.Approved) {
        // End Date not defined
        if (customer.endDate) {
          const today = getCurrentDateOnly();
          const endDate: Date = stringToDate(customer.endDate);

          // If Today's Date is smaller than the End date the Customer is Active, otherwise it is Inactive
          status = today < endDate ? CustomerStatus.Active : CustomerStatus.Inactive;
        } else {
          status = CustomerStatus.Active;
        }
      }

      return status;
    }

    return null;
  };

  const getStatusClass = (status: CustomerStatus): string => {
    // If Approved, the Status will be displayed as either Active or Inactive

    switch (status) {
      case CustomerStatus.Active:
        return 'customerStatusActive';

      case CustomerStatus.Inactive:
        return 'customerStatusInactive';

      case CustomerStatus.Pending:
        return 'customerStatusPending';

      case CustomerStatus.Draft:
        return 'customerStatusDraft';

      default:
        return null;
    }
  };

  // Error has Occured
  const onCloseCustomerInfo = (): void => {
    // Direct back to Customers
    setRedirect('/customers');
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Certificate was Approved in Exceptions Tab - Customer is Auto Approved
  const onCertificateApproved = () => {
    // Update the Customer Status Indicator
    setCustomerWasApproved(true);
  };

  // Certificate was Edited in Exceptions Tab - Reload as contacts may may been modified
  const onCertificateEdited = (certificate: Certificate) => {
    Logging.Info('onCertificateEdited');

    // Request Refresh of the Customer
    refreshCustomer(selectedTab);
  };

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  Logging.Debug('Customer Info', uuid, customer);

  const customerName = customer ? customer.name : 'Customer Name';
  const customerParentName = customer && customer.parent ? customer.parent.name : 'Parent Customer';
  const customerParentUuid = customer && customer.parent ? customer.parent.uuid : null;
  const isGeneralTabSelected = selectedTab === '1';
  const status: CustomerStatus = getCustomerStatus();
  const showEditCustomerButton = permissionCustomersEdit && (ccPreventEcmEdits !== true);
  const showAddCertificateButton = false;
  const showApproveButton = (status === CustomerStatus.Pending) && permissionCustomersEdit && (ccPreventEcmEdits !== true);

  // The Child Customers Tab is not valid for a Child Customer
  if (hasParentCustomer && (selectedTab === '4')) {
    setSelectedTab('1');
  }

  return (
    <>
      <div className="customerInfoContainer">
        <div className="customerInfoHeader">
          <div className="customerInfoHeaderBackToCustomers">
            <VertexLink href="#/customers" display="Back to Customers" />
          </div>
          <div className="customerInfoHeaderParent">
            {
              hasParentCustomer && (
                <>
                  <VertexLink
                    href={`#/customer/view/${customerParentUuid}/1`}
                    display={`${customerParentName} >`}
                    additionalLinkStyleClass="customerInfoParentCustomerLink"
                  />
                </>
              )
            }
          </div>
          <div className="customerInfoHeaderMain">
            <div className="customerInfoHeaderMainLeft">
              <div className="customerHeaderName" title={customerName}>
                <span>{customerName}</span>
              </div>
            </div>
            <div className="customerInfoHeaderMainRight">
              <div className={`customerInfoHeaderItemBase customerHeaderStatus ${getStatusClass(status)}`}>
                {status}
              </div>
              {
                showEditCustomerButton && (
                  <div className="customerInfoHeaderItemBase">
                    <VtxButton
                      id="editCustomerButton"
                      icon={<VtxIcon type="edit" />}
                      type="default"
                      onClick={onEditCustomer}
                    >
                      Edit Customer
                    </VtxButton>
                  </div>
                )
              }
              {
                showAddCertificateButton && (
                  <div className="customerInfoHeaderItemBase">
                    <VtxButton
                      id="addCertificateButton"
                      icon={<VtxIcon type="plus" />}
                      type="default"
                      onClick={onAddCertificate}
                    >
                      Add Certificate
                    </VtxButton>
                  </div>
                )
              }
              {
                isGeneralTabSelected && showApproveButton && (
                  <>
                    <div className="customerInfoHeaderItemBase">
                      <VtxButton
                        id="approveButton"
                        icon={<VtxIcon type="check-circle" />}
                        type="primary"
                        className="greenButton"
                        onClick={onApprove}
                      >
                        Approve
                      </VtxButton>
                    </div>
                  </>
                )
              }
            </div>
          </div>
        </div>
        <div className="customerInfoContentContainer">
          <CustomerViewTabs
            customer={customer}
            selectedTab={selectedTab}
            onSelectTab={onSelectTab}
            hasParentCustomer={hasParentCustomer}
            gatewayEndPoint={gatewayEndPoint}
            onCertificateApproved={onCertificateApproved}
            onCertificateEdited={onCertificateEdited}
          />
        </div>
      </div>
      <CustomerApproveConfirmation
        isOpen={isCustomerApproveConfirmationOpen}
        onCancel={onCancelConfirmation}
        onApprove={onApproveConfirmation}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default CustomerView;
